import type { ReactNode } from "react";
import React from "react";
import { AppConfigContext } from "../app/appConfig";
import { useAlert } from "../app/notificationToast";
import { NewsletterModel } from "../../api/newsletter/NewsletterModel";
import { NewsletterAdapter } from "../../api/newsletter/NewsletterAdapter";

/** Mapping: group name -> list of email types or labels */
export type NewsletterMapping = { [key: string]: string[] };

interface NewsletterContextType {
  getNewsletterMapping: () => Promise<NewsletterMapping | undefined>;
  updateNewsletterMapping: (mapping: NewsletterMapping) => Promise<void>;
}

const NewsletterContext = React.createContext<NewsletterContextType>(null!);

export function NewsletterProvider(props: { children: ReactNode }) {
  const appConfig = React.useContext(AppConfigContext);
  const { showAlert } = useAlert();

  const adapter = React.useMemo(() => {
    if (!appConfig) return null;
    const model = new NewsletterModel(appConfig);
    return new NewsletterAdapter(model);
  }, [appConfig]);

  const getNewsletterMapping = React.useCallback(async (): Promise<
    NewsletterMapping | undefined
  > => {
    if (!adapter) return undefined;
    try {
      return await adapter.getNewsletterMapping();
    } catch (err) {
      showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      return undefined;
    }
  }, [adapter, showAlert]);

  const updateNewsletterMapping = React.useCallback(
    async (mapping: NewsletterMapping): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.updateNewsletterMapping(mapping);
        showAlert("Groupes Google enregistrés", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const value = React.useMemo<NewsletterContextType>(
    () => ({
      getNewsletterMapping,
      updateNewsletterMapping,
    }),
    [getNewsletterMapping, updateNewsletterMapping],
  );

  return <NewsletterContext.Provider value={value}>{props.children}</NewsletterContext.Provider>;
}

export function useNewsletter(): NewsletterContextType {
  const ctx = React.useContext(NewsletterContext);
  if (!ctx) throw new Error("useNewsletter must be used within NewsletterProvider");
  return ctx;
}
